import { useCallback, useEffect, useRef, useState } from "react";
import { useAudioRecorder } from "@shared/hooks/useAudioRecorder";
import { useAudioPlayer } from "@shared/hooks/useAudioPlayer";

export interface CallSummary {
    session_id: string;
    site: string;
    location: string;
    machine: string;
    state: "with_fenna" | "escalation_requested" | "with_operator" | "ended";
    urgency: "low" | "normal" | "high" | "emergency";
    elapsed_s: number;
    last_caller_text: string;
    last_bot_text: string;
    caller_plate: string;
    has_operator: boolean;
}

export type ConsoleState =
    | "idle"
    | "connecting"
    | "listening_dashboard"
    | "taking_over"
    | "on_call"
    | "ended"
    | "error";

export interface UseOperatorConsoleResult {
    state: ConsoleState;
    error: string | null;
    calls: CallSummary[];
    activeCall: CallSummary | null;
    muted: boolean;
    toggleMute: () => void;
    takeOver: (sessionId: string) => Promise<void>;
    hangup: () => Promise<void>;
    connect: () => Promise<void>;
}

/**
 * Operator dashboard hook.
 *
 * Opens /ws/operator, receives a rolling list of active kiosk calls, and lets
 * the operator "take over" a specific call. After takeover the same WS is used
 * as the audio peer for the kiosk browser (24 kHz PCM16 mono, no header).
 */
export function useOperatorConsole(operatorName: string): UseOperatorConsoleResult {
    const [state, setState] = useState<ConsoleState>("idle");
    const [error, setError] = useState<string | null>(null);
    const [calls, setCalls] = useState<CallSummary[]>([]);
    const [activeCall, setActiveCall] = useState<CallSummary | null>(null);
    const [muted, setMuted] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const activeSessionRef = useRef<string | null>(null);
    const mutedRef = useRef(muted);
    mutedRef.current = muted;

    const handleMicRef = useRef<(chunk: ArrayBuffer) => void>(() => {});
    handleMicRef.current = (chunk: ArrayBuffer) => {
        if (mutedRef.current) return;
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN && activeSessionRef.current) {
            ws.send(chunk);
        }
    };
    const stableMic = useCallback((chunk: ArrayBuffer) => {
        handleMicRef.current(chunk);
    }, []);

    // Kiosk mic frames arrive as raw 24 kHz PCM16, no header — same rate we
    // configure the shared player with.
    const player = useAudioPlayer(24000);
    const recorder = useAudioRecorder(stableMic);
    const playerRef = useRef(player);
    const recorderRef = useRef(recorder);
    playerRef.current = player;
    recorderRef.current = recorder;

    const teardown = useCallback(async () => {
        activeSessionRef.current = null;
        setActiveCall(null);
        try { await recorderRef.current.stop(); } catch { /* noop */ }
        try { await playerRef.current.cleanup(); } catch { /* noop */ }
        if (wsRef.current) {
            try { wsRef.current.close(); } catch { /* noop */ }
            wsRef.current = null;
        }
    }, []);

    const connect = useCallback(async () => {
        if (wsRef.current) return;
        setError(null);
        setState("connecting");

        const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
        const token = new URLSearchParams(window.location.search).get("token") || "";
        const qs = token ? `?token=${encodeURIComponent(token)}` : "";
        const ws = new WebSocket(`${scheme}//${window.location.host}/ws/operator${qs}`);
        ws.binaryType = "arraybuffer";
        wsRef.current = ws;

        ws.onopen = () => setState("listening_dashboard");

        ws.onmessage = async (ev) => {
            if (ev.data instanceof ArrayBuffer) {
                await playerRef.current.play(ev.data);
                return;
            }
            let evt: Record<string, unknown>;
            try { evt = JSON.parse(ev.data); } catch { return; }
            switch (evt.type) {
                case "calls": {
                    const list = (evt.calls as CallSummary[]) || [];
                    setCalls(list);
                    // Track the active call so its transcript preview updates
                    // while the operator is on it.
                    const active = activeSessionRef.current;
                    if (active) {
                        const match = list.find((c) => c.session_id === active) || null;
                        if (match) setActiveCall(match);
                    }
                    break;
                }
                case "take_over_ok": {
                    activeSessionRef.current = evt.session_id as string;
                    // Recorder was already started in the click handler (inside
                    // the user-gesture window) so it's ready before we get here.
                    setState("on_call");
                    break;
                }
                case "take_over_failed": {
                    setError(`Kon niet overnemen: ${evt.reason ?? "unknown"}`);
                    // Roll back the mic we opened in the click handler.
                    try { await recorderRef.current.stop(); } catch { /* noop */ }
                    setState("listening_dashboard");
                    break;
                }
            }
        };

        ws.onerror = () => {
            setError("Verbinding met dispatcher verbroken");
            setState("error");
        };

        ws.onclose = () => {
            setState((s) => (s === "error" ? s : "ended"));
            wsRef.current = null;
            activeSessionRef.current = null;
            void recorderRef.current.stop().catch(() => {});
        };
    }, []);

    const takeOver = useCallback(async (sessionId: string) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        setError(null);
        setState("taking_over");
        try {
            // Must happen INSIDE the click's user-gesture window; if we wait
            // for the take_over_ok round-trip, the browser blocks the mic
            // prompt because the click has already dispatched.
            await recorderRef.current.start();
        } catch (e) {
            const err = e as DOMException;
            const name = err?.name || "Error";
            setError(`Microfoontoegang [${name}]: ${err?.message || String(e)}`);
            setState("listening_dashboard");
            return;
        }
        ws.send(JSON.stringify({
            type: "take_over",
            session_id: sessionId,
            operator_name: operatorName,
        }));
    }, [operatorName]);

    const hangup = useCallback(async () => {
        setState("ended");
        await teardown();
    }, [teardown]);

    const toggleMute = useCallback(() => {
        setMuted((m) => !m);
    }, []);

    useEffect(() => {
        return () => { void teardown(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { state, error, calls, activeCall, muted, toggleMute, takeOver, hangup, connect };
}
